import React, { useEffect } from 'react';
import StarMapGenerator from '../starmap/StarMapGenerator';

// Import cities data to look up city names
import { citiesData } from '../../utils/cityData';

// Shared style mapping function
const mapStyleName = (style) => {
    // Handle null/undefined styles
    if (!style) return 'stellar simplicity';

    // Convert to lowercase and handle spaces for comparison
    const normalizedStyle = String(style).toLowerCase().trim();

    // List of valid style names
    const validStyles = [
        'stellar simplicity',
        'orbit',
        'constellation frame',
        'celestial corners',
        'starlit halo',
        'lunar harmony',
        'astro mechanics',
        'cosmic crown'
    ];

    // Find a matching style (case insensitive)
    const matchedStyle = validStyles.find(valid =>
        valid.toLowerCase() === normalizedStyle
    );

    if (matchedStyle) {
        return matchedStyle;
    }

    // Default to stellar simplicity if no match
    console.log(`Style "${style}" not recognized, defaulting to stellar simplicity`);
    return 'stellar simplicity';
};

// SVG for the decorative orbit elements that surround the map
const OrbitalDecorations = ({ style = 'stellar simplicity' }) => {
    // Get the normalized style for SVG selection
    const normalizedStyle = mapStyleName(style);

    // Debug log to help diagnose issues
    console.log(`OrbitalDecorations - Using style: "${normalizedStyle}"`);

    // Choose the SVG source path based on style
    const getSvgPath = (style) => {
        switch (style) {
            case 'stellar simplicity': return process.env.PUBLIC_URL + '/assets/images/Design1_white.svg';
            case 'constellation frame': return process.env.PUBLIC_URL + '/assets/images/Design3_white.svg';
            case 'celestial corners': return process.env.PUBLIC_URL + '/assets/images/Design4_white.svg';
            case 'starlit halo': return process.env.PUBLIC_URL + '/assets/images/Design5_white.svg';
            case 'lunar harmony': return process.env.PUBLIC_URL + '/assets/images/Design6_white.svg';
            case 'astro mechanics': return process.env.PUBLIC_URL + '/assets/images/Design7_white.svg';
            case 'cosmic crown': return process.env.PUBLIC_URL + '/assets/images/Design8_white.svg';
            case 'orbit': return process.env.PUBLIC_URL + '/assets/images/Design2_white.svg';
            default: return process.env.PUBLIC_URL + '/assets/images/Design1_white.svg';
        }
    };

    // Get style-specific CSS adjustments for the <img> element
    const getStyleAdjustments = (style) => {
        switch (style) {
            case 'stellar simplicity':
                return { transform: 'scale(1)' };
            case 'orbit':
                return { transform: 'scale(1)', objectFit: 'fill' };
            case 'constellation frame':
                return { transform: 'scale(1)' };
            case 'celestial corners':
                return { transform: 'scale(1)' };
            case 'starlit halo':
                return { transform: 'scale(1)' };
            case 'lunar harmony':
                return { transform: 'scale(1)' };
            case 'astro mechanics':
                return { transform: 'scale(1)' };
            case 'cosmic crown':
                return { transform: 'scale(1)', objectFit: 'fill', padding: '0' };
            default:
                return { transform: 'scale(1)' };
        }
    };

    const styleAdjustments = getStyleAdjustments(normalizedStyle);

    return (
        <img
            src={getSvgPath(normalizedStyle)}
            className="w-full h-full"
            style={styleAdjustments}
            alt="Orbital decoration"
        />
    );
};

// Component to render the caption text below the map
const MapCaption = ({ caption, location, date }) => {
    // Format the date
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    // Find city name from coordinates
    const getCityNameFromCoordinates = (locationCoords) => {
        if (!locationCoords) return 'Location';

        try {
            // Parse coordinates
            const [lat, lng] = locationCoords.split(',').map(coord => parseFloat(coord.trim()));

            // Find matching city
            const city = citiesData.find(city =>
                Math.abs(city.lat - lat) < 0.01 && Math.abs(city.lng - lng) < 0.01
            );

            return city ? city.name : 'Location';
        } catch (error) {
            return 'Location';
        }
    };

    // Get city name from coordinates
    const cityName = getCityNameFromCoordinates(location);

    return (
        <div className="text-center mt-4 mb-2 text-white/80" style={{ marginTop: '20px' }}>
            <div className="font-light text-lg mb-1">{caption || 'Special Text'}</div>
            <div className="text-xs opacity-70">
                {cityName}<br />
                {formatDate(date)}
            </div>
        </div>
    );
};

// Background styles based on the selection
const getBackgroundStyle = (backgroundType) => {
    if (!backgroundType) return {};

    const backgrounds = {
        'basic-light': 'linear-gradient(120deg, #e5e7eb 0%, #f3f4f6 100%)',
        'basic-dark': 'linear-gradient(120deg, #6b7280 0%, #9ca3af 100%)',
        'sunset-pink': 'linear-gradient(120deg, #f87171 0%, #fecaca 100%)',
        'sunset-orange': 'linear-gradient(120deg, #fb923c 0%, #fed7aa 100%)',
        'pastel-pink': 'linear-gradient(120deg, #f9a8d4 0%, #fce7f3 100%)',
        'pastel-blue': 'linear-gradient(120deg, #93c5fd 0%, #dbeafe 100%)',
        'pastel-white': 'linear-gradient(120deg, #f9fafb 0%, #f3f4f6 100%)',
        'neon-purple': 'radial-gradient(circle, rgba(192,38,211,1) 0%, rgba(253,29,191,0.7) 40%, rgba(69,10,94,1) 100%)',
        'neon-blue': 'radial-gradient(circle, rgba(59,130,246,1) 0%, rgba(37,99,235,1) 40%, rgba(30,58,138,1) 100%)',
        'natural-gold': 'linear-gradient(120deg, #fcd34d 0%, #fef3c7 100%)',
        'natural-brown': 'linear-gradient(120deg, #d97706 0%, #fbbf24 100%)',
        'natural-beige': 'linear-gradient(120deg, #fef3c7 0%, #f9fafb 100%)',
        'golden-dark': 'radial-gradient(circle, rgba(180,83,9,1) 0%, rgba(66,30,15,1) 60%, rgba(0,0,0,1) 100%)',
        'golden-black': 'radial-gradient(circle, rgba(75,85,99,1) 0%, rgba(31,41,55,1) 50%, rgba(17,24,39,1) 80%, rgba(0,0,0,1) 100%)',
        'golden-gold': 'radial-gradient(circle, rgba(252,211,77,1) 0%, rgba(217,119,6,1) 60%, rgba(120,53,15,1) 100%)'
    };

    return { background: backgrounds[backgroundType] || 'black' };
};

const getMapShapeStyles = (style) => {
    // Always use circular shape as requested
    return {
        borderRadius: '50%'
    };
};

// Get wrapper style adjustments based on the orbital style
const getWrapperStyleAdjustments = (style) => {
    // Handle null/undefined styles
    if (!style) return {};

    // Get normalized style name
    const normalizedStyle = mapStyleName(style);

    // Return style adjustments based on the design
    switch (normalizedStyle) {
        case 'stellar simplicity':
            return { padding: '24px' };
        case 'orbit':
            return { padding: '24px' };
        case 'constellation frame':
            return { padding: '24px' };
        case 'celestial corners':
            return { padding: '36px' };
        case 'starlit halo':
            return { padding: '22px' };
        case 'lunar harmony':
            return { padding: '18px' };
        case 'astro mechanics':
            return { padding: '26px' };
        case 'cosmic crown':
            return { padding: '0' };
        default:
            return { padding: '24px' };
    }
};

const MapPreview = ({
    formData,
    isGenerating,
    loadingProgress,
    mapGenerated,
    mapError,
    handleStarMapError,
    handleStarMapLoaded,
    shouldRegenerateMap,
    starMapRef
}) => {
    // Debug log for formData.orbitalStyle
    console.log('MapPreview - orbitalStyle:', formData.orbitalStyle);

    // Get the background style based on selection
    const backgroundStyle = getBackgroundStyle(formData.background);

    // Get shape styles for the map container
    const mapShapeStyles = getMapShapeStyles(formData.orbitalStyle);

    // Add CSS to hide zoom controls
    useEffect(() => {
        // Add a style to hide zoom controls and fix SVG object positioning
        const style = document.createElement('style');
        style.textContent = `
            #celestial-zoomin, #celestial-zoomout {
                display: none !important;
                visibility: hidden !important;
            }
            
            /* Ensure SVG objects properly fit their containers */
            object[type="image/svg+xml"] {
                width: 100%;
                height: 100%;
                display: block;
            }
            
            /* Ensure proper rendering of orbital decorations */
            .orbital-decoration-wrapper {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                z-index: 100;
                pointer-events: none;
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: visible;
                padding: 24px;
            }
            
            /* Ensure the SVG inside scales properly */
            .orbital-decoration-wrapper object {
                width: 100%;
                height: 100%;
                display: block;
                object-fit: contain;
                max-width: 100%;
                max-height: 100%;
            }

            /* Ensure SVG objects properly fit their containers */
            .orbital-decoration-wrapper img {
                width: 100%;
                height: 100%;
                display: block;
                object-fit: cover;
                max-width: 100%;
                max-height: 100%;
                image-rendering: auto;
                filter: drop-shadow(0 0 2px rgba(255,255,255,0.1));
            }
            
            /* Ensure decorations and background elements are visible when captured by html2canvas */
            .poster-bg-container {
                position: relative;
                z-index: 1;
                transform: translateZ(0);
                -webkit-transform: translateZ(0);
                overflow: hidden;
            }
            
            /* Force proper rendering of SVG decorations */
            .orbital-decoration-wrapper svg,
            .orbital-decoration-wrapper img {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                transform: translateZ(0);
                -webkit-transform: translateZ(0);
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Add a useEffect to log when formData changes
    useEffect(() => {
        console.log('MapPreview - formData location or date changed:',
            { location: formData.location, date: formData.date });

        // When location or date changes, set regenerate flag
        // shouldRegenerateMap is a ref object, not a boolean condition
        shouldRegenerateMap.current = true;

        // Don't directly set isGenerating here - let the parent component handle loading state
        // The parent will handle proper animation timing
    }, [formData.location, formData.date, shouldRegenerateMap]);

    return (
        <div className="bg-black/30 rounded-lg overflow-hidden !w-full" style={{ paddingBottom: '30px' }}>
            {/* SVG definitions for clip paths */}
            <svg width="0" height="0" style={{ position: 'absolute' }}>
                <defs>
                    <clipPath id="heartClip">
                        <path d="M100,177 C 60,127 0,92 0,42 C 0,17 18,7 35,7 C 67,7 100,37 100,37 C 100,37 133,7 165,7 C 182,7 200,17 200,42 C 200,92 140,127 100,177 Z" />
                    </clipPath>
                </defs>
            </svg>

            {/* Framed poster preview */}
            <div className="relative w-full">
                {/* Frame */}
                <div className="relative bg-black shadow-xl rounded-lg aspect-[3/4]">
                    {/* Poster with mat/border */}
                    <div className="relative poster-bg-container bg-white h-full w-full rounded p-4 sm:p-6" style={{ backgroundColor: "white" }}>
                        {/* Decorative orbital elements - positioned as absolute overlay inside the poster container */}
                        <div
                            className="orbital-decoration-wrapper"
                            style={getWrapperStyleAdjustments(formData.orbitalStyle)}
                        >
                            <OrbitalDecorations style={formData.orbitalStyle} />
                        </div>

                        {/* Poster background with map */}
                        <div
                            className="relative h-full w-full overflow-hidden rounded"
                            style={backgroundStyle}
                        >
                            {/* Map circular container positioned near the top */}
                            <div
                                className="circular-map absolute left-1/2 transform -translate-x-1/2"
                                style={{
                                    top: '105px',
                                    width: '75%',
                                    height: 'auto',
                                    // paddingBottom: '10px',
                                }}
                            >
                                <div
                                    className="relative aspect-square overflow-hidden bg-black"
                                    style={{
                                        ...mapShapeStyles,
                                    }}
                                >
                                    {/* Loading indicator overlay (absolute positioned to avoid layout shifts) */}
                                    {isGenerating && (
                                        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/20 backdrop-blur-[1px] rounded-full transition-all duration-500 ease-in-out">
                                            <div className="w-2/3 bg-black/40 rounded-lg px-4 py-3 shadow-lg transition-opacity duration-300">
                                                <div className="w-full h-2 bg-black/20 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-gradient-to-r from-accent to-accent/80 transition-all duration-700 ease-in-out"
                                                        style={{ width: `${loadingProgress}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Actual star map content */}
                                    <div
                                        className="absolute overflow-hidden"
                                        style={{
                                            ...mapShapeStyles,
                                            zIndex: 1,
                                            top: 0,
                                            right: 0,
                                            bottom: 0,
                                            left: 0
                                        }}
                                    >
                                        {mapError ? (
                                            <div className="text-center py-8 px-4 text-red-400 h-full flex items-center justify-center">
                                                <p>Error generating star map: {mapError}</p>
                                            </div>
                                        ) : (
                                            <StarMapGenerator
                                                ref={starMapRef}
                                                date={formData.date}
                                                location={formData.location}
                                                caption={formData.caption}
                                                mapStyle={formData.mapStyle}
                                                size={800}
                                                showStars={formData.showStars}
                                                showConstellations={formData.showConstellations}
                                                showConstellationNames={formData.showConstellationNames}
                                                showGrid={formData.showGrid}
                                                showDeepSkyObjects={formData.showDeepSkyObjects}
                                                showEcliptic={formData.showEcliptic}
                                                onError={handleStarMapError}
                                                onLoaded={handleStarMapLoaded}
                                                regenerate={shouldRegenerateMap.current}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Caption positioned at bottom with absolute positioning */}
                            <div
                                className="absolute w-full left-1/2 transform -translate-x-1/2"
                                style={{
                                    bottom: '60px',
                                    zIndex: 10,
                                }}
                            >
                                <MapCaption
                                    caption={formData.caption}
                                    location={formData.location}
                                    date={formData.date}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MapPreview; 